import { Button, Stack, Box, Grid, CircularProgress, Alert, Snackbar, Typography, Modal, Divider, Popover, Chip, Dialog, DialogContent, Tabs, Tab } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRenderEditCellParams, GridValueFormatter } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import React, { useEffect, useState } from 'react';
import { RootState } from '../../../reducers';
import { fetchMemberStatusChanges, insertMemberStatusChanges, updateMemberStatusChanges } from '../../../store/memberDetail.slice';
import { setShowEditMemberStatusHistory } from '../../../store/memberDemographicsDetails.slice';
import { useHealthPlanContext } from '../../../hooks/healthPlanContext';
import { isDefaultDate } from '../../../utils/common';
import { localDateFormat } from '../../../utils/timeFormat';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Delete from '@mui/icons-material/Delete';
import { modalStyle } from '../../../utils/constants';
import { TextField, MenuItem, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MemberStatusChange from '../../../Models/Member/MemberStatusChange.model';
import { fetchMemberDemoGraphicInfo } from '../../../store/memberDetail.slice';
import { MemberStatuses } from '../../../utils/mappings';
import Authorization from '../../../utils/Authorization';

const MemberStatusHistory = () => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { healthPlans } = useHealthPlanContext();
    const { memberStatusChanges, selectedMember, isFetching, isDeleting } = useSelector((state: RootState) => state.memberDetail);
    const { memberStatuses } = useSelector((state: RootState) => state.members);
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [rows, setRows] = useState([]);
    const [editedRows, setEditedRows] = useState<
        {
            rowKey: string | number;
            Id?: number | null; // ✅ real DB Id
            isDeleted: boolean;
        }[]
    >([]);
    const [rowModesModel, setRowModesModel] = useState({});
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [deletedId, setDeletedId] = useState('');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [templateOpen, setTemplateOpen] = useState(false);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const open = Boolean(anchorEl);
    const [newStatus, setNewStatus] = useState<MemberStatusChange>({} as MemberStatusChange);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [tab, setTab] = useState(0);

    const isFormValid = () => {
        return (
            newStatus.OldStatus !== null &&
            newStatus.OldStatus !== undefined &&
            newStatus.RequestedStatus !== null &&
            newStatus.RequestedStatus !== undefined &&
            !!newStatus.HealthPlan &&
            !!newStatus.RequestedDate &&
            !!newStatus.EffectiveDate &&
            !!newStatus.ApprovedDate
        );
    };

    const openStatusList = [
        ...memberStatuses.filter((s) =>
            MemberStatuses.some(
                (os) =>
                    String(os.Id) === String(s.value) &&
                    os.IsOpen
            )
        ),
        {
            value: MemberStatusEnum.OffBoarding.toString(),
            label: MemberStatusEnum.OffBoardingDescription
        }
    ].sort((a, b) => a.label.localeCompare(b.label));

    const closedStatusList = memberStatuses
        .filter((s) => MemberStatuses.some((os) => String(os.Id) === String(s.value) && !os.IsOpen))
        .sort((a, b) => a.label.localeCompare(b.label));

    const sortByEffectiveDate = (list) =>
        [...list].sort(
            (a, b) => new Date(a.EffectiveDate).getTime() - new Date(b.EffectiveDate).getTime()
        );

    const filteredHealthPlans = healthPlans
        .filter((item) => {
            return population.map((item) => item.HealthPlan?.Name).includes(item.Name);
        })
        .sort((a, b) => a.Name.localeCompare(b.Name));

    useEffect(() => {
        dispatch(fetchMemberStatusChanges(selectedMember.Id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setRows(memberStatusChanges);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberStatusChanges]);

    const HealthPlanEditCell = (params: GridRenderEditCellParams) => {
        return (
            <TextField
                select
                fullWidth
                value={params.value || ''}
                onChange={(e) => {
                    const newValue = e.target.value || null;
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: newValue,
                    });
                }}
                InputProps={{
                    endAdornment: params.value ? (
                        <InputAdornment position="end" sx={{ pr: 2 }}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    params.api.setEditCellValue({
                                        id: params.id,
                                        field: params.field,
                                        value: null,
                                    });
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                }}
            >
                {filteredHealthPlans.map((plan) => (
                    <MenuItem key={plan.Id} value={plan.Id}>
                        {plan.Name}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const toLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day, 12, 0, 0); // ⬅ noon
    };

    const columns: GridColDef[] = [
        {
            field: 'OldStatus',
            headerName: 'Old Status',
            minWidth: 210,
            type: 'singleSelect',
            editable: true,
            valueOptions: memberStatuses,
            valueFormatter: ((value) =>
                value === 0 || value == null
                    ? '-'
                    : MemberStatusEnum[value as number]
            ) as GridValueFormatter,
            sortable: false,
        },
        {
            field: 'RequestedStatus',
            headerName: 'Requested Status',
            minWidth: 210,
            type: 'singleSelect',
            editable: true,
            valueOptions: memberStatuses,
            valueFormatter: ((value) =>
                value === 0 || value == null
                    ? '-'
                    : MemberStatusEnum[value as number]
            ) as GridValueFormatter,
            sortable: false,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            minWidth: 200,
            type: 'singleSelect',
            editable: true,
            valueOptions: filteredHealthPlans.map((plan) => ({
                value: plan.Id, // backend value
                label: plan.Name, // display text in dropdown
            })),
            valueGetter: (value, row) => {
                const id = row.HealthPlanId ?? (row.HealthPlan?.Id ? String(row.HealthPlan.Id) : null);
                const plan = filteredHealthPlans.find((p) => String(p.Id) === id);
                return plan ? plan.Id : '-';
            },
            valueSetter: (value, row) => {
                const selectedPlan = filteredHealthPlans.find((plan) => plan.Id === value) || null;
                return {
                    ...row,
                    HealthPlan: selectedPlan,
                };
            },
            renderCell: (params: GridRenderCellParams) => {
                const plan = filteredHealthPlans.find((p) => String(p.Id) === String(params.value));
                return <>{plan ? plan.Name : '-'}</>;
            },
            renderEditCell: (params) => <HealthPlanEditCell {...params} />,
            sortable: false,
        },
        {
            field: 'RequestedDate',
            headerName: 'Requested Date',
            minWidth: 135,
            editable: true,
            type: 'date',
            valueFormatter: ((value) =>
                isDefaultDate(value) ? '-' : localDateFormat(new Date(value))
            ) as GridValueFormatter,
            sortable: false,
        },
        {
            field: 'EffectiveDate',
            headerName: 'Effective Date',
            minWidth: 135,
            editable: true,
            type: 'date',
            valueFormatter: ((value) =>
                isDefaultDate(value) ? '-' : localDateFormat(new Date(value))
            ) as GridValueFormatter,
            sortable: false,
        },
        {
            field: 'ApprovedDate',
            headerName: 'Approved Date',
            minWidth: 135,
            editable: true,
            type: 'date',
            valueFormatter: ((value) =>
                isDefaultDate(value) ? '-' : localDateFormat(new Date(value))
            ) as GridValueFormatter,
            sortable: false,
        },
        {
            field: 'RequestedBy',
            headerName: 'Requested By',
            minWidth: 135,
            valueGetter: (value, row) => `${row.RequestedBy === null ? '-' : row.RequestedBy.FullName}`,
            sortable: false,
        },
        {
            field: 'ApprovedBy',
            headerName: 'Approved By',
            minWidth: 135,
            valueGetter: (value, row) => `${row.ApprovedBy === null ? '-' : row.ApprovedBy.FullName}`,
            sortable: false,
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 30,
            sortable: false,
            cellClassName: 'no-focus-outline',
            renderCell: (params: GridRenderCellParams) => {
                const isInEditMode = rowModesModel[params.id]?.mode === 'edit';

                if (isInEditMode) {
                    return (
                        <>
                            <DoneIcon
                                sx={{ cursor: 'pointer', marginRight: '20px' }}
                                color="primary"
                                onClick={(event) => {
                                    handleSaveClick(params.id);
                                }}
                            />
                            <CancelIcon
                                sx={{ cursor: 'pointer' }}
                                color="primary"
                                onClick={(event) => {
                                    event.stopPropagation(); // prevent row click event
                                    handleCancelClick(params.id);
                                }}
                            />
                        </>
                    );
                } else {
                    return (
                        <>
                            <EditIcon
                                sx={{ cursor: 'pointer' }}
                                color="primary"
                                onClick={(event) => {
                                    event.stopPropagation(); // prevent row click event
                                    handleEditClick(params.id);
                                }}
                            />
                            <Delete
                                sx={{ cursor: 'pointer' }}
                                color="primary"
                                className="icon-right"
                                onClick={() => {
                                    setDeletedId(params.row.Id ?? params.row._tempId);
                                    setShowDelete(true);
                                }}
                            />
                        </>
                    );
                }
            },
        },
    ];

    const handleCancelClick = (id) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: 'view', ignoreModifications: true },
        }));
    };

    const handleSaveClick = (id) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: 'view' },
        }));
    };

    const handleEditClick = (id) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: 'edit' },
        }));
    };

    const cancel = () => {
        dispatch(setShowEditMemberStatusHistory(false));
    };

    const getRowKey = (row) => row.Id ?? row._tempId;

    const upsertEditedRow = (prev, newItem) => {
        const index = prev.findIndex(e => e.rowKey === newItem.rowKey);

        if (index !== -1) {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                ...newItem
            };
            return updated;
        }

        return [...prev, newItem];
    };

    const handleProcessRowUpdate = (updatedRow) => {
        const rowKey = getRowKey(updatedRow);
        setRows((prev) =>
            sortByEffectiveDate(
                prev.map((row) =>
                    getRowKey(row) === rowKey
                        ? { ...row, ...updatedRow }
                        : row
                )
            )
        );
        setEditedRows(prev =>
            upsertEditedRow(prev, {
                rowKey,
                Id: updatedRow.Id,
                isDeleted: false
            })
        );
        return updatedRow;
    };

    const handleAddNewRow = (newRow) => {
        const rowKey = crypto.randomUUID();
        const rowWithTempId = {
            ...newRow,
            rowKey,
            _tempId: rowKey,
            Id: null,
            MemberId: selectedMember.Id,
            RequestedBy: {
                Id: authData.UserId,
                FullName: authData.UserName,
            },
            ApprovedBy: {
                Id: authData.UserId,
                FullName: authData.UserName,
            },
            isDeleted: false,
        };

        const updatedRows = sortByEffectiveDate([...rows, rowWithTempId]);
        setRows(updatedRows);

        setEditedRows(prev =>
            upsertEditedRow(prev, {
                ...rowWithTempId,   // ✅ store full row
                isDeleted: false
            })
        );

        handleCloseCreate();
    };

    const handleOnDelete = () => {
        const row = rows.find(
            (r) =>
                (r.Id ?? r._tempId) === deletedId
        );

        if (!row) return;

        setRows(prev =>
            prev.filter(
                r => (r.Id ?? r._tempId) !== deletedId
            )
        );

        setEditedRows(prev =>
            upsertEditedRow(prev, {
                ...row,
                rowKey: getRowKey(row),
                Id: row.Id ?? null,
                isDeleted: true
            })
        );

        setShowDelete(false);
    };

    const handleSaveAll = () => {
        //const updatedRows = rows.filter((row) => editedRows.includes(row.Id ?? row._tempId));
        const updatedRows = editedRows.map((edited) => {
            const { rowKey, Id, isDeleted } = edited;

            // Try to get from current rows (works for add/edit)
            const row = rows.find(r => getRowKey(r) === rowKey);

            // 🗑 REAL DELETE (existing DB row)
            if (isDeleted && Id) {
                return {
                    ...edited,
                    isDeleted: true
                };
            }

            // New row added then deleted → ignore completely
            if (isDeleted && !Id) {
                return null;
            }

            // INSERT: new row (Id is null)
            if (!Id) {
                return {
                    ...row,
                    Id: null,
                    isDeleted: false
                };
            }

            // UPDATE
            return {
                ...row,
                Id,
                isDeleted: false
            };
        });

        let isValid = true;

        // Check date validation
        for (let i = 1; i < rows.length; i++) {
            const current = new Date(rows[i].EffectiveDate);
            const previous = new Date(rows[i - 1].EffectiveDate);

            current.setHours(0, 0, 0, 0);
            previous.setHours(0, 0, 0, 0);

            if (!current || !previous || current < previous) {
                isValid = false;
                setErrorMessage('Effective date should not be less than the last entry effective date');
                setShowAlert(true);
                return;
            }
        }

        // Check status validation
        for (let i = 1; i < rows.length; i++) {
            const prevRequestedStatus = rows[i - 1].RequestedStatus;
            const currentOldStatus = rows[i].OldStatus;

            if (parseInt(prevRequestedStatus) !== parseInt(currentOldStatus)) {
                isValid = false;
                setErrorMessage("Each row's old status must match the previous row's requested status.");
                setShowAlert(true);
                return;
            }
        }

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const requested = row?.RequestedStatus;
            const old = row?.OldStatus;

            if (!Boolean(requested) || !Boolean(old)) {
                isValid = false;
                setErrorMessage('Old and requested status should not be null in any row.');
                setShowAlert(true);
                return;
            }

            if (parseInt(requested) === parseInt(old)) {
                isValid = false;
                setErrorMessage('Old and requested status are not the same in any row.');
                setShowAlert(true);
                return;
            }
        }

        if (isValid) {
            dispatch(
                updateMemberStatusChanges(updatedRows.filter(r => r !== null), () => {
                    dispatch(fetchMemberStatusChanges(selectedMember.Id));
                    setEditedRows([]);
                    setShowSuccess(true);
                })
            );
        }
    };

    useEffect(() => {
        if (open) {
            setTab(0);
        }
    }, [open]);

    const handleDiscard = () => {
        setRows(memberStatusChanges);
        setEditedRows([]);
    };

    const handleOpenCreate = () => setOpenCreateModal(true);
    const handleCloseCreate = () => {
        setOpenCreateModal(false);
        setIsSubmitted(false);
        setNewStatus({} as MemberStatusChange);
    };

    const handleOnInsert = () => {
        setIsSubmitted(true);
        newStatus.MemberId = selectedMember.Id;
        dispatch(
            insertMemberStatusChanges(newStatus, () => {
                handleCloseCreate();
                dispatch(fetchMemberStatusChanges(selectedMember.Id));
                dispatch(fetchMemberDemoGraphicInfo(selectedMember.Id));
                setEditedRows([]);
                setShowSuccess(true);
                setIsSubmitted(false);
            })
        );
    };

    const estimatedActivePeriods = React.useMemo(() => {
        if (!rows || rows.length === 0) return [];

        // Sort rows by EffectiveDate
        const sortedRows = rows
            .filter(r => r.EffectiveDate)
            .sort(
                (a, b) =>
                    new Date(a.EffectiveDate).getTime() -
                    new Date(b.EffectiveDate).getTime()
            );

        const activeStatusValue = MemberStatusEnum.Active.toString();

        const isClosedStatus = (status) =>
            closedStatusList.some(
                s => s.value.toString() === status.toString()
            );

        const activeRows = sortedRows.filter(
            r => r.RequestedStatus.toString() === activeStatusValue
        );

        const closedRows = sortedRows.filter(
            r => isClosedStatus(r.RequestedStatus)
        );

        // If NO closed statuses exist
        if (closedRows.length === 0) {
            if (activeRows.length === 0) return [];

            return [
                {
                    startDate: activeRows[0].EffectiveDate,
                    endDate: null
                }
            ];
        }

        const activePeriods = [];
        let closedIndex = 0;
        let lastClosedDate = null;

        for (let i = 0; i < activeRows.length; i++) {
            const activeItem = activeRows[i];
            const activeStart = new Date(activeItem.EffectiveDate);

            // Skip overlapping active
            if (lastClosedDate && activeStart < lastClosedDate) {
                continue;
            }

            let endDate = null;

            // Find next closed >= active start
            while (
                closedIndex < closedRows.length &&
                new Date(closedRows[closedIndex].EffectiveDate) < activeStart
            ) {
                closedIndex++;
            }

            if (closedIndex < closedRows.length) {
                endDate = closedRows[closedIndex].EffectiveDate;
                lastClosedDate = new Date(closedRows[closedIndex].EffectiveDate);
                closedIndex++;
            } else {
                lastClosedDate = null;
            }

            activePeriods.push({
                startDate: activeItem.EffectiveDate,
                endDate: endDate
            });
        }

        return activePeriods;

    }, [rows, closedStatusList]);

    const renderDeleteBody = () => {
        return (
            <Stack spacing={2}>
                <div className="row">
                    <Typography variant="h6">Are you sure do you want to delete this history?</Typography>
                </div>
                <Box>
                    <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                        <Button
                            className="button-120"
                            variant="contained"
                            color="error"
                            onClick={() => handleOnDelete()}
                            disabled={isDeleting}
                            endIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            Delete
                        </Button>
                        <Button
                            className="button-120"
                            variant="outlined"
                            onClick={() => {
                                setShowDelete(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        );
    };

    return (
        <div>
            <Grid container spacing={1}>
                <Grid size={12} minHeight="67vh" sx={{ position: 'relative' }}>
                    {isFetching ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Stack spacing={2}>
                                <Stack direction="row" sx={{ width: '100%' }}>
                                    {/* Left side */}
                                    <Box sx={{ display: 'flex' }}>
                                        <Button
                                            onClick={handleSaveAll}
                                            variant="contained"
                                            color="primary"
                                            sx={{ m: 1 }}
                                            disabled={editedRows?.length === 0}
                                        >
                                            Save All
                                        </Button>
                                        <Button
                                            onClick={handleDiscard}
                                            variant="outlined"
                                            color="secondary"
                                            sx={{ m: 1 }}
                                            disabled={editedRows?.length === 0}
                                        >
                                            Discard Changes
                                        </Button>
                                        <Button
                                            onClick={handleOpenCreate}
                                            variant="contained"
                                            color="success"
                                            sx={{ m: 1 }}
                                        >
                                            Add New
                                        </Button>
                                    </Box>

                                    {/* Right side */}
                                    <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
                                        <Chip
                                            sx={{ marginTop: '10px' }}
                                            label="Active Periods"
                                            icon={<CalendarMonthIcon />}
                                            color="success"
                                            variant="outlined"
                                            clickable
                                            onClick={(e) => setAnchorEl(e.currentTarget)}
                                        />
                                        <Button onClick={() => setTemplateOpen(true)}>
                                            Status History Template
                                        </Button>
                                    </Box>
                                </Stack>
                                <div style={{ width: '100%', height: '55vh' }}>
                                    <DataGrid
                                        getRowId={(row) => row.Id ?? row._tempId}
                                        rows={rows}
                                        columns={columns}
                                        editMode="row"
                                        rowModesModel={rowModesModel}
                                        onRowModesModelChange={setRowModesModel}
                                        processRowUpdate={handleProcessRowUpdate}
                                        experimentalFeatures={{ newEditingApi: true } as any}
                                        hideFooter
                                        slots={
                                            {
                                                noRowsOverlay: () => (
                                                    <Stack height="100%" alignItems="center" justifyContent="center">
                                                        There is no status history for this member.
                                                    </Stack>
                                                ),
                                            } as any
                                        }
                                        sx={{
                                            '& .no-focus-outline:focus': {
                                                outline: 'none',
                                            },
                                            '& .no-focus-outline:focus-within': {
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                </div>
                            </Stack>
                            <Snackbar
                                style={{ position: 'absolute', top: '8px', right: '0' }}
                                open={showSuccess}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                autoHideDuration={3000}
                                onClose={() => setShowSuccess(false)}
                            >
                                <Alert variant="filled" severity={'success'} sx={{ width: '100%' }} onClose={() => setShowSuccess(false)}>
                                    The status history has been updated successfully!
                                </Alert>
                            </Snackbar>
                            <Snackbar
                                style={{ position: 'absolute', top: '8px', right: '0' }}
                                open={showAlert}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                autoHideDuration={5000}
                                onClose={() => setShowAlert(false)}
                            >
                                <Alert variant="filled" severity={'error'} sx={{ width: '100%' }} onClose={() => setShowAlert(false)}>
                                    {errorMessage}
                                </Alert>
                            </Snackbar>
                            <Stack direction="row" spacing={2} style={{ float: 'right', paddingTop: '15px' }}>
                                <Button variant="outlined" onClick={cancel} style={{ marginBottom: '0' }}>
                                    Close
                                </Button>
                            </Stack>
                        </>
                    )}
                </Grid>
            </Grid>
            {/* Add new status history */}
            <Modal open={openCreateModal} onClose={handleCloseCreate}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 520,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                    }}
                >
                    {/* Header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                    >
                        <Typography variant="h6" fontWeight={600}>
                            Create Status History
                        </Typography>

                        <IconButton onClick={handleCloseCreate}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Form */}
                    <Stack spacing={2.5}>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                {/* Old Status */}
                                <TextField
                                    select
                                    required
                                    error={isSubmitted && newStatus.OldStatus === null}
                                    helperText={isSubmitted && newStatus.OldStatus === null ? 'Required' : ''}
                                    label="Old Status"
                                    value={newStatus.OldStatus ?? ''}
                                    onChange={(e) =>
                                        setNewStatus({ ...newStatus, OldStatus: Number(e.target.value) })
                                    }
                                    fullWidth
                                >
                                    {memberStatuses.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={6}>
                                {/* Requested Status */}
                                <TextField
                                    select
                                    required
                                    error={isSubmitted && newStatus.RequestedStatus === null}
                                    helperText={isSubmitted && newStatus.RequestedStatus === null ? 'Required' : ''}
                                    label="Requested Status"
                                    value={newStatus.RequestedStatus ?? ''}
                                    onChange={(e) =>
                                        setNewStatus({ ...newStatus, RequestedStatus: Number(e.target.value) })
                                    }
                                    fullWidth
                                >
                                    {memberStatuses.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={6}>
                                {/* Health Plan */}
                                <TextField
                                    select
                                    required
                                    error={isSubmitted && !newStatus.HealthPlan}
                                    helperText={isSubmitted && !newStatus.HealthPlan ? 'Required' : ''}
                                    label="Health Plan"
                                    value={newStatus.HealthPlan?.Id ?? ''}
                                    onChange={(e) =>
                                        setNewStatus({
                                            ...newStatus,
                                            HealthPlan: filteredHealthPlans.find(
                                                (plan) => plan.Id === e.target.value
                                            ),
                                        })
                                    }
                                    fullWidth
                                >
                                    {filteredHealthPlans.map((plan) => (
                                        <MenuItem key={plan.Id} value={plan.Id}>
                                            {plan.Name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={6}>
                                {/* Requested Date */}
                                <TextField
                                    type="date"
                                    required
                                    error={isSubmitted && !newStatus.RequestedDate}
                                    helperText={isSubmitted && !newStatus.RequestedDate ? 'Required' : ''}
                                    label="Requested Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                        newStatus.RequestedDate instanceof Date
                                            ? newStatus.RequestedDate.toISOString().split('T')[0]
                                            : ''
                                    }
                                    onChange={(e) =>
                                        setNewStatus({
                                            ...newStatus,
                                            RequestedDate: e.target.value
                                                ? toLocalDate(e.target.value) // ✅ FIX
                                                : null,
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={6}>
                                {/* Effective Date */}
                                <TextField
                                    type="date"
                                    required
                                    error={isSubmitted && !newStatus.EffectiveDate}
                                    helperText={isSubmitted && !newStatus.EffectiveDate ? 'Required' : ''}
                                    label="Effective Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                        newStatus.EffectiveDate instanceof Date
                                            ? newStatus.EffectiveDate.toISOString().split('T')[0]
                                            : ''
                                    }
                                    onChange={(e) =>
                                        setNewStatus({
                                            ...newStatus,
                                            EffectiveDate: e.target.value
                                                ? toLocalDate(e.target.value) // ✅ FIX
                                                : null,
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={6}>
                                {/* Approved Date */}
                                <TextField
                                    type="date"
                                    required
                                    error={isSubmitted && !newStatus.ApprovedDate}
                                    helperText={isSubmitted && !newStatus.ApprovedDate ? 'Required' : ''}
                                    label="Approved Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                        newStatus.ApprovedDate instanceof Date
                                            ? newStatus.ApprovedDate.toISOString().split('T')[0]
                                            : ''
                                    }
                                    onChange={(e) =>
                                        setNewStatus({
                                            ...newStatus,
                                            ApprovedDate: e.target.value
                                                ? toLocalDate(e.target.value) // ✅ FIX
                                                : null,
                                        })
                                    }
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Stack>

                    {/* Footer */}
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        mt={3}
                    >
                        <Button onClick={handleCloseCreate}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!isFormValid()}
                            // onClick={() => {
                            //     if (!isFormValid()) return;
                            //     openStatusList.some(s => s.value === newStatus.RequestedStatus.toString()) ? handleOnInsert() : setShowConfirm(true);
                            // }}
                            onClick={() => {
                                if (!isFormValid()) return;
                                handleAddNewRow(newStatus);
                            }}
                        >
                            {isSubmitted ? 'Saving...' : 'Save'}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
            {/* Delete member status change */}
            <Modal
                open={showDelete}
                onClose={() => {
                    setShowDelete(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Delete Status History</Typography>
                        <Divider />
                        {renderDeleteBody()}
                    </Stack>
                </Box>
            </Modal>
            {/* Active Periods */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: 6,
                    },
                }}
            >
                <Box sx={{ p: 2.5, width: 320 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 0.5 }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Active Period History
                        </Typography>

                        <button
                            onClick={() => setAnchorEl(null)}
                            aria-label="Close"
                            style={{
                                background: 'transparent',
                                border: 'none',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            X
                        </button>
                    </Stack>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 2, display: 'block' }}
                    >
                        Member active date ranges
                    </Typography>

                    <Divider />

                    <Tabs
                        value={tab}
                        onChange={(_, value) => setTab(value)}
                        variant="fullWidth"
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Current" />
                        <Tab label="Estimated" />
                    </Tabs>

                    {tab === 0 && (
                        <Stack spacing={1.5} sx={{ maxHeight: 250, overflowY: 'auto' }}>
                            {/* Header row */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ px: 1.5 }}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 70 }}
                                >
                                    Start Date
                                </Typography>

                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 20 }}
                                >
                                    →
                                </Typography>

                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 100 }}
                                >
                                    End Date
                                </Typography>
                            </Stack>

                            {memberStatusChanges.filter(m => (m.RequestedStatus === MemberStatusEnum.Active && Boolean(m.ActiveStartDate))).map((period, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        backgroundColor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center">
                                        <Typography variant="body2" sx={{ width: 70 }}>
                                            {isDefaultDate(period.ActiveStartDate)
                                                ? '-'
                                                : localDateFormat(new Date(period.ActiveStartDate))}
                                        </Typography>

                                        <Typography sx={{ mx: 1 }}>→</Typography>

                                        <Typography variant="body2" sx={{ width: 120 }}>
                                            {(isDefaultDate(period.ActiveEndDate)) && openStatusList.some(s => s.value === selectedMember.Status.toString())
                                                ? 'Present'
                                                : localDateFormat(new Date(period.ActiveEndDate))}
                                        </Typography>

                                        {period.ActiveEndDate === null && openStatusList.some(s => s.value === selectedMember.Status.toString()) && (
                                            <Chip
                                                label="Active"
                                                size="small"
                                                color="success"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                    {tab === 1 && (
                        <Stack spacing={1.5} sx={{ maxHeight: 250, overflowY: 'auto' }}>
                            {/* Header row */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                sx={{ px: 1.5 }}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 70 }}
                                >
                                    Start Date
                                </Typography>

                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 20 }}
                                >
                                    →
                                </Typography>

                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ width: 100 }}
                                >
                                    End Date
                                </Typography>
                            </Stack>

                            {estimatedActivePeriods &&
                                estimatedActivePeriods.length > 0 &&
                                editedRows.length > 0 ?
                                estimatedActivePeriods.map((period, index) => (
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            backgroundColor: 'grey.50',
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center">
                                            <Stack key={index} direction="row" alignItems="center">
                                                <Typography variant="body2" sx={{ width: 70 }}>
                                                    {localDateFormat(new Date(period.startDate))}
                                                </Typography>

                                                <Typography sx={{ mx: 1 }}>→</Typography>

                                                <Typography variant="body2" sx={{ width: 120 }}>
                                                    {period.endDate
                                                        ? localDateFormat(new Date(period.endDate))
                                                        : 'Present'}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )

                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        No estimated active period available
                                    </Typography>
                                )}
                        </Stack>
                    )}
                </Box>
            </Popover>
            {/* Confirmation for closed status */}
            <Modal
                open={showConfirm}
                onClose={() => {
                    setShowConfirm(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Change Member Status</Typography>
                        <Divider />
                        <Stack spacing={2}>
                            <div className="row">
                                <Typography sx={{ fontSize: '17px' }}>Your requested status is a closed status. This action will change the member’s current status to closed. Do you want to proceed?</Typography>
                            </div>
                            <Box>
                                <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                                    <Button
                                        className="button-120"
                                        variant="contained"
                                        color="error"
                                        onClick={() => { setShowConfirm(false); handleOnInsert() }}
                                        disabled={isDeleting}
                                        endIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : null}
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        className="button-120"
                                        variant="outlined"
                                        onClick={() => {
                                            setShowConfirm(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    </Stack>
                </Box>
            </Modal>
            {/* History Template */}
            {templateOpen && (
                <Dialog open>
                    <DialogContent className="max-w-md">
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 0',
                            borderBottom: '1px solid #ccc'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                                Status History Order
                            </h2>

                            <button
                                onClick={() => setTemplateOpen(false)}
                                aria-label="Close"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                X
                            </button>
                        </div>
                        <div style={{ marginTop: '12px', fontSize: '14px', lineHeight: '1.5' }}>
                            <p style={{ marginBottom: '8px' }}>
                                The following is a sample order for updating the status history. Each active status must be closed before initiating the next active status.
                            </p>
                            <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0 }}>
                                <li style={{ marginBottom: '15px', marginTop: '15px' }}>
                                    <strong>Undetermined</strong> → <strong>Scheduled</strong>
                                </li>
                                <li style={{ marginBottom: '15px' }}>
                                    <strong>Scheduled</strong> → <strong>Active</strong>
                                </li>
                                <li style={{ marginBottom: '15px' }}>
                                    <strong>Active</strong> → <strong>OffBoarding</strong>
                                </li>
                                <li style={{ marginBottom: '15px' }}>
                                    <strong>OffBoarding</strong> → <strong>Uncompensated Care</strong>
                                </li>
                                <li>
                                    <strong>Uncompensated Care</strong> → <strong>Disenrolled From CHP</strong>
                                </li>
                            </ul>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>

    );
};

export default MemberStatusHistory;
